import { apiClient } from './client';
import type { DocumentItem, DocumentTemplate, DocumentAnnotation } from '../types';

export const documentsApi = {
  getDocuments: async (entityType: string, entityId: number): Promise<DocumentItem[]> => {
    const { data } = await apiClient.get(`/${entityType}/${entityId}/documents`);
    return data;
  },

  uploadDocument: async (entityType: string, entityId: number, file: File): Promise<DocumentItem> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post(`/${entityType}/${entityId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  generateDocument: async (entityType: string, entityId: number, templateName: string): Promise<DocumentItem> => {
    const { data } = await apiClient.post(`/${entityType}/${entityId}/documents/generate`, {
      template_name: templateName,
    });
    return data;
  },

  downloadDocument: async (documentId: number, filename: string): Promise<void> => {
    const response = await apiClient.get(`/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Fetch a document as a blob URL for inline viewing (PDF/image). Caller is
  // responsible for URL.revokeObjectURL when the view is dismissed.
  getViewBlobUrl: async (documentId: number): Promise<string> => {
    const response = await apiClient.get(`/documents/${documentId}/view`, {
      responseType: 'blob',
    });
    return window.URL.createObjectURL(response.data as Blob);
  },

  updateDocument: async (
    documentId: number,
    updates: Partial<Pick<DocumentItem, 'notes'>>
  ): Promise<DocumentItem> => {
    const { data } = await apiClient.patch(`/documents/${documentId}`, updates);
    return data;
  },

  renameDocument: async (documentId: number, filename: string): Promise<DocumentItem> => {
    const { data } = await apiClient.patch(`/documents/${documentId}/rename`, { filename });
    return data;
  },

  deleteDocument: async (documentId: number): Promise<void> => {
    await apiClient.delete(`/documents/${documentId}`);
  },

  getTemplates: async (): Promise<DocumentTemplate[]> => {
    const { data } = await apiClient.get('/templates');
    return data;
  },

  // --- Annotations ---

  getAnnotations: async (documentId: number): Promise<DocumentAnnotation[]> => {
    const { data } = await apiClient.get(`/documents/${documentId}/annotations`);
    return data;
  },

  createAnnotation: async (
    documentId: number,
    annotation: { page_number: number | null; x_pct: number; y_pct: number; body: string }
  ): Promise<DocumentAnnotation> => {
    const { data } = await apiClient.post(`/documents/${documentId}/annotations`, annotation);
    return data;
  },

  updateAnnotation: async (annotationId: number, body: string): Promise<DocumentAnnotation> => {
    const { data } = await apiClient.patch(`/annotations/${annotationId}`, { body });
    return data;
  },

  deleteAnnotation: async (annotationId: number): Promise<void> => {
    await apiClient.delete(`/annotations/${annotationId}`);
  },
};
